const MOBILE_QUERY = '(max-width: 999px)';
const IO_MARGIN = '300px';

class LazyVideoMedia extends HTMLElement {
  #observer = null;
  #loaded = false;
  #video = null;
  #soundToggle = null;
  #mutedIcon = null;
  #unmutedIcon = null;
  #srLabel = null;
  #abortController = new AbortController();

  connectedCallback() {
    this.#video = this.querySelector('.lazy-video__element');
    this.#soundToggle = this.querySelector('.lazy-video__sound-toggle');
    this.#mutedIcon = this.querySelector('.lazy-video__sound-toggle-icon--muted');
    this.#unmutedIcon = this.querySelector('.lazy-video__sound-toggle-icon--unmuted');
    this.#srLabel = this.#soundToggle?.querySelector('.sr-only');

    if (!this.#video || this.#shouldSkipVideo()) {
      return;
    }

    const signal = this.#abortController.signal;

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.#loadAndPlay();
        }
      },
      { rootMargin: IO_MARGIN }
    );

    this.#observer.observe(this);
    this.#checkAlreadyVisible();

    this.#soundToggle?.addEventListener('click', this.#toggleSound.bind(this), { signal });

    const slideshow = this.closest('slideshow-carousel');
    slideshow?.addEventListener('carousel:change', this.#onCarouselChange.bind(this), { signal });
    slideshow?.addEventListener('player:pause', () => this.pause(), { signal });
    slideshow?.addEventListener('player:resume', () => this.play(), { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#observer?.disconnect();
  }

  play({ restart = false } = {}) {
    if (!this.#video || this.#shouldSkipVideo()) {
      return;
    }

    if (!this.#loaded) {
      this.#loadAndPlay();
      return;
    }

    if (restart) {
      this.#video.currentTime = 0;
    }

    this.#video.play().catch(() => {});
  }

  pause() {
    this.#video?.pause();
    this.removeAttribute('playing');
  }

  #shouldSkipVideo() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return true;
    }

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    if (connection?.saveData) {
      return true;
    }

    if (connection?.effectiveType === '2g' || connection?.effectiveType === 'slow-2g') {
      return true;
    }

    return false;
  }

  #isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  #checkAlreadyVisible() {
    const rect = this.getBoundingClientRect();
    const margin = 300;
    const isVisible = rect.bottom >= -margin && rect.top <= window.innerHeight + margin;

    if (isVisible) {
      this.#loadAndPlay();
    }
  }

  #readSources(type) {
    const script = this.querySelector(`script[data-lazy-video-sources="${type}"]`);

    if (!script) {
      return [];
    }

    try {
      const sources = JSON.parse(script.textContent.trim());
      return Array.isArray(sources) ? sources.filter((source) => source?.url) : [];
    } catch (error) {
      console.warn('lazy-video-media: failed to parse sources', error);
      return [];
    }
  }

  #getSources() {
    const mobileSources = this.#readSources('mobile');
    const desktopSources = this.#readSources('desktop');

    if (this.#isMobile() && mobileSources.length > 0) {
      return mobileSources;
    }

    return desktopSources;
  }

  #pickSource(sources) {
    if (!sources.length) {
      return null;
    }

    return sources.reduce((best, source) => {
      if (!best) {
        return source;
      }

      return source.width > best.width ? source : best;
    }, null);
  }

  #injectSource() {
    if (this.#loaded || !this.#video) {
      return;
    }

    const sources = this.#getSources();
    const source = this.#pickSource(sources);

    if (!source) {
      console.warn('lazy-video-media: no playable source found', this);
      return;
    }

    const sourceElement = document.createElement('source');
    sourceElement.src = source.url.startsWith('//') ? `https:${source.url}` : source.url;
    sourceElement.type = source.mime_type || 'video/mp4';
    this.#video.appendChild(sourceElement);
    this.#video.load();
    this.#loaded = true;
    this.setAttribute('loaded', '');
  }

  #loadAndPlay() {
    if (this.#shouldSkipVideo()) {
      return;
    }

    this.#injectSource();

    if (!this.#loaded) {
      return;
    }

    this.#observer?.disconnect();
    this.#video.preload = 'auto';

    this.#video.addEventListener(
      'playing',
      () => {
        this.setAttribute('playing', '');
      },
      { once: true }
    );

    this.#video.play().catch(() => {});
  }

  #toggleSound() {
    if (!this.#video || !this.#soundToggle) {
      return;
    }

    this.#video.muted = !this.#video.muted;
    const isMuted = this.#video.muted;

    this.#soundToggle.setAttribute('aria-pressed', String(!isMuted));
    this.#mutedIcon.hidden = !isMuted;
    this.#unmutedIcon.hidden = isMuted;

    if (this.#srLabel) {
      this.#srLabel.textContent = isMuted
        ? window.themeVariables?.strings?.unmuteVideo || 'Unmute video'
        : window.themeVariables?.strings?.muteVideo || 'Mute video';
    }
  }

  #onCarouselChange(event) {
    const isActiveSlide = event.detail.cell?.contains(this);

    if (isActiveSlide) {
      this.play({ restart: true });
    } else {
      this.pause();
    }
  }
}

if (!window.customElements.get('lazy-video-media')) {
  window.customElements.define('lazy-video-media', LazyVideoMedia);
}
